import { useContext, useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context";
import { LANDING_URL } from "../env";
import Login from "../views/Auth/Login";
import ForgotPassword from "../views/Auth/ForgotPassword";
import ResetPassword from "../views/Auth/ResetPassword";
import Register from "../views/Auth/Register";
import RegisterSuccess from "../views/Auth/RegisterSuccess";
import VerifySocialAccount from "../views/Auth/VerifySocialAccount";
import Dashboard from "../views/Dashboard/Index";
import Account from "../views/MyAccount/Account";
import ContentList from "../views/MyContent/Index";
import ContentCreate from "../views/MyContent/Create";
import ContentDetail from "../views/MyContent/Detail";
import ContentEdit from "../views/MyContent/Edit";
import ProductCollectionsBrowse from "../views/ProductCollections/Index";
import ProductCollectionsCheckout from "../views/ProductCollections/Checkout";
import MyGifts from "../views/MyGifts/Index";
import GiftOrdersList from "../views/GiftOrders/Index";
import GiftOrderDetail from "../views/GiftOrders/Detail";
import Notifications from "../views/Notifications/Index";
import Help from "../views/Help/Index";
import HelpContact from "../views/Help/Contact";
import Messages from "../views/Messages/Index";
import Aura from "../views/Aura/Index";
import AuraFeedbackSurvey from "../views/Aura/FeedbackSurvey";
import Error from "../views/Other/Error";
import TermsAndConditions from "../views/Other/TermsAndConditions";
import TermsConditions from "../views/Other/TermsConditions";
import PrivacyPolicy from "../views/Other/PrivacyPolicy";
import SocialVerificationCallback from "../views/Auth/SocialVerificationCallback";
import ProtectedRoute from "./ProtectedRoute";
import UnProtectedRoute from "./UnProtectedRoute";
import LoadingState from "../components/Common/LoadingState";
import { loadPublicTranslations } from "../utils/loadPublicTranslations";
import { loadTranslations } from "../utils/loadTranslations";
import { LANGUAGE_STORAGE_KEY, normalizeLanguageCode } from "../i18n";

const LandingRedirect = () => { useEffect(() => { window.location.replace(LANDING_URL); }, []); return null; };

const PUBLIC_TRANSLATION_ROUTES = new Set([
    "/",
    "/login",
    "/forgot-password",
    "/reset-password",
    "/register",
    "/register-success",
    "/verify-social",
    "/terms",
    "/social-verification/callback",
    "/terms-conditions",
    "/privacy-policy",
]);

const AppRoutes = () => {
    const { isAuthenticated } = useContext(AuthContext);
    const location = useLocation();
    const { i18n } = useTranslation();
    const [translationsReady, setTranslationsReady] = useState(false);

    useEffect(() => {
        const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (stored) return;

        const params = new URLSearchParams(window.location.search);
        const urlLang = params.get("lang");
        const lang = urlLang?.startsWith("el") ? "gr"
                   : urlLang === "en" ? "en"
                   : navigator.language?.startsWith("el") ? "gr" : "en";

        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
        i18n.changeLanguage(lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const language = normalizeLanguageCode(
            localStorage.getItem(LANGUAGE_STORAGE_KEY) ||
            i18n.language ||
            i18n.resolvedLanguage
        );

        const shouldUsePublicTranslations =
            PUBLIC_TRANSLATION_ROUTES.has(location.pathname) || !isAuthenticated;

        if (shouldUsePublicTranslations) {
            loadPublicTranslations(language).finally(() => setTranslationsReady(true));
            return;
        }

        const loadAuthenticatedTranslations = async () => {
            await loadPublicTranslations(language);
            await loadTranslations(language);
            setTranslationsReady(true);
        };

        loadAuthenticatedTranslations();
    }, [isAuthenticated, location.pathname, i18n.language, i18n.resolvedLanguage]);

    return (
        <>
        {!translationsReady && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
                <LoadingState />
            </div>
        )}
        <Routes>
            <Route path="/" element={<LandingRedirect />} />
            <Route path="/login" element={<UnProtectedRoute isAuthenticated={isAuthenticated}><Login /></UnProtectedRoute>} />
            <Route path="/forgot-password" element={<UnProtectedRoute isAuthenticated={isAuthenticated}><ForgotPassword /></UnProtectedRoute>} />
            <Route path="/reset-password" element={<UnProtectedRoute isAuthenticated={isAuthenticated}><ResetPassword /></UnProtectedRoute>} />
            <Route path="/register" element={<UnProtectedRoute isAuthenticated={isAuthenticated}><Register /></UnProtectedRoute>} />
            <Route path="/register-success" element={<UnProtectedRoute isAuthenticated={isAuthenticated}><RegisterSuccess /></UnProtectedRoute>} />
            {/* Standalone TikTok verification demo — unguarded (no auth requirement) so it can be recorded for TikTok's app review video */}
            <Route path="/verify-social" element={<VerifySocialAccount />} />
            <Route path="/dashboard" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Dashboard /></ProtectedRoute>} />
            <Route path="/my-account" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Account /></ProtectedRoute>} />
            <Route path="/my-content" element={<ProtectedRoute isAuthenticated={isAuthenticated}><ContentList /></ProtectedRoute>} />
            <Route path="/my-content/new" element={<ProtectedRoute isAuthenticated={isAuthenticated}><ContentCreate /></ProtectedRoute>} />
            <Route path="/my-content/:contentId" element={<ProtectedRoute isAuthenticated={isAuthenticated}><ContentDetail /></ProtectedRoute>} />
            <Route path="/my-content/:contentId/edit" element={<ProtectedRoute isAuthenticated={isAuthenticated}><ContentEdit /></ProtectedRoute>} />
            <Route path="/product-collections" element={<ProtectedRoute isAuthenticated={isAuthenticated}><ProductCollectionsBrowse /></ProtectedRoute>} />
            <Route path="/product-collections/checkout" element={<ProtectedRoute isAuthenticated={isAuthenticated}><ProductCollectionsCheckout /></ProtectedRoute>} />
            <Route path="/my-gifts" element={<ProtectedRoute isAuthenticated={isAuthenticated}><MyGifts /></ProtectedRoute>} />
            <Route path="/gift-orders" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GiftOrdersList /></ProtectedRoute>} />
            <Route path="/gift-orders/:orderId" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GiftOrderDetail /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Messages /></ProtectedRoute>} />
            <Route path="/messages/new" element={<ProtectedRoute isAuthenticated={isAuthenticated}><HelpContact activeLabel="Messages" forceNewChat /></ProtectedRoute>} />
            <Route path="/messages/:messageId" element={<ProtectedRoute isAuthenticated={isAuthenticated}><HelpContact activeLabel="Messages" /></ProtectedRoute>} />
            <Route path="/aura-ai-assistant" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Aura /></ProtectedRoute>} />
            <Route path="/aura-ai-assistant/feedback" element={<ProtectedRoute isAuthenticated={isAuthenticated}><AuraFeedbackSurvey /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Notifications /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Help /></ProtectedRoute>} />
            <Route path="/help/contact" element={<ProtectedRoute isAuthenticated={isAuthenticated}><HelpContact /></ProtectedRoute>} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/social-verification/callback" element={<SocialVerificationCallback />} />
            <Route path="*" element={<Error />} />
        </Routes>
        </>
    )
}

export default AppRoutes
